import { type Vector2 } from 'three';
import { type StopHandle } from '@/util/stopHandle';

/**
 * Result of projecting a pointer event into world space and detecting a hit.
 * `hit === null` means "no draggable target under the pointer".
 */
export type ProjectedPointer<H> = {
    hit: H | null;
    world: Vector2;
};

export type PointerProjection<H> = (event: PointerEvent) => ProjectedPointer<H>;

export type PointerDragOptions<H> = {
    canvas: HTMLCanvasElement;

    /**
     * Project a pointer event into world coordinates and run hit-detection.
     * Returning `{ hit: null, ... }` means "no interaction" — pointerdown is ignored,
     * and pointermove (when no drag is active) routes through `onHover`.
     */
    pointerProjection: PointerProjection<H>;

    /** Mouse button to respond to. Default 0 (left). */
    button?: number;

    /**
     * CSS pixels of pointer movement that promote a click into a drag.
     * Default 0 — drag starts immediately on pointerdown when there is a hit.
     * When > 0, a pointerdown without subsequent movement past the threshold
     * is treated as a click (calls `onClick`, never calls `onDragStart` / `onDrag`).
     */
    dragThresholdPx?: number;

    /** Called on pointermove when no drag is active. */
    onHover?: (hit: H | null, world: Vector2, event: PointerEvent) => void;

    /**
     * Called on pointerdown over a hit, before the threshold is considered —
     * for affordances that must answer the press itself (a grabbing cursor)
     * rather than promotion. Always paired with one `onPressEnd`.
     */
    onPress?: (hit: H, grabWorld: Vector2) => void;

    /**
     * Called when that press ends (pointerup / cancel / lost capture), promoted or not.
     * `release` is the projection at the release point, null on cancel / lost capture.
     * Prefer it over `pressedHit`: a drag that clamps leaves its target behind.
     *
     * Paired with each `onPress`, except on teardown — see the stop handle.
     */
    onPressEnd?: (pressedHit: H, release: ProjectedPointer<H> | null) => void;

    /** Called once when a drag begins, after the threshold check. `world` is the press position. */
    onDragStart?: (hit: H, grabWorld: Vector2) => void;

    /** Called per drag update (rAF-throttled). `world` is the current pointer position. */
    onDrag: (hit: H, world: Vector2) => void;

    /** Called when a drag ends via pointerup / pointercancel / lostpointercapture. */
    onDragEnd?: (hit: H) => void;

    /** Called on pointerup when the drag never promoted past the threshold. */
    onClick?: (hit: H, grabWorld: Vector2) => void;

    /** Called on pointerleave (e.g. for cursor / hover-state reset). */
    onPointerLeave?: () => void;

    /**
     * Toggle camera controls: `false` on drag start, `true` on drag end.
     * Optional — callers can drive this externally (e.g. via a Pinia watcher
     * on their own drag state) and omit this option entirely.
     */
    setControlsEnabled?: (enabled: boolean) => void;
};

/** A press not yet promoted to a drag. `screenX`/`screenY` is the threshold origin. */
type PendingPress<H> = { hit: H; grabWorld: Vector2; screenX: number; screenY: number };

/**
 * Callback ORDER is load-bearing: `hipSpineSyncStore` debounces its save off the return to idle,
 * and `setControlsEnabled(true)` releases the camera. A dropped or doubled end-of-drag loses a
 * landmark edit. Every `onPress` gets exactly one `onPressEnd`.
 */
type DragMachine<H> = {
    readonly options: PointerDragOptions<H>;
    pending: PendingPress<H> | null;
    active: { hit: H } | null;
    /** Outlives promotion: starts on pointerdown, ends on release, drag or not. */
    pressed: H | null;
    lastWorld: Vector2 | null;
    pendingFrame: number | null;
};

function createDragMachine<H>(options: PointerDragOptions<H>): DragMachine<H> {
    return {
        options,
        pending: null,
        active: null,
        pressed: null,
        lastWorld: null,
        pendingFrame: null,
    };
}

function cancelScheduled<H>(m: DragMachine<H>): void {
    if (m.pendingFrame !== null) {
        cancelAnimationFrame(m.pendingFrame);
        m.pendingFrame = null;
    }
}

function promote<H>(m: DragMachine<H>): void {
    if (!m.pending) return;
    const { hit, grabWorld } = m.pending;
    m.pending = null;
    m.active = { hit };
    m.options.setControlsEnabled?.(false);
    m.options.onDragStart?.(hit, grabWorld);
}

function flushDrag<H>(m: DragMachine<H>): void {
    if (!m.active || !m.lastWorld) return;
    m.options.onDrag(m.active.hit, m.lastWorld);
}

/** Call after any end-of-drag callback that rebuilds the scene — projecting runs the hit-test. */
function endPress<H>(releaseEvent: PointerEvent | null, m: DragMachine<H>): void {
    if (m.pressed === null) return;
    const hit = m.pressed;
    m.pressed = null;
    m.options.onPressEnd?.(
        hit,
        releaseEvent === null ? null : m.options.pointerProjection(releaseEvent),
    );
}

function endActiveDrag<H>(m: DragMachine<H>): void {
    if (!m.active) return;
    const { hit } = m.active;
    m.active = null;
    cancelScheduled(m);
    m.options.setControlsEnabled?.(true);
    m.options.onDragEnd?.(hit);
}

/** pointerdown on a hit. A zero threshold promotes straight to a drag. */
function beginPress<H>(press: PendingPress<H>, m: DragMachine<H>): void {
    m.pending = press;
    // A second touch displaces the press in flight; close it so every onPress keeps its pair.
    endPress(null, m);
    m.pressed = press.hit;
    m.options.onPress?.(press.hit, press.grabWorld);

    if ((m.options.dragThresholdPx ?? 0) === 0) {
        promote(m);
    }
}

/**
 * @returns whether the move was consumed; `false` routes it to `onHover`. A press below the
 * threshold still consumes — that is a press in progress, not hover.
 */
function moveTo<H>(
    at: { world: Vector2; screenX: number; screenY: number },
    m: DragMachine<H>,
): boolean {
    if (m.pending) {
        const dx = at.screenX - m.pending.screenX;
        const dy = at.screenY - m.pending.screenY;
        const threshold = m.options.dragThresholdPx ?? 0;
        if (dx * dx + dy * dy <= threshold * threshold) {
            return true;
        }
        promote(m);
    }

    if (m.active) {
        m.lastWorld = at.world;
        if (m.pendingFrame === null) {
            m.pendingFrame = requestAnimationFrame(() => {
                m.pendingFrame = null;
                flushDrag(m);
            });
        }
        return true;
    }

    return false;
}

/** An unpromoted press is a click; otherwise end the drag. */
function release<H>(event: PointerEvent, m: DragMachine<H>): void {
    if (m.pending) {
        const { hit, grabWorld } = m.pending;
        m.pending = null;
        m.options.onClick?.(hit, grabWorld);
        endPress(event, m);
        return;
    }
    endActiveDrag(m);
    endPress(event, m);
}

/** pointercancel / lostpointercapture: no click, and no projection for the release. */
function abort<H>(m: DragMachine<H>): void {
    m.pending = null;
    endActiveDrag(m);
    endPress(null, m);
}

/** Drops scheduled work and state without firing callbacks; callers clean up themselves. */
function disposeMachine<H>(m: DragMachine<H>): void {
    cancelScheduled(m);
    m.pending = null;
    m.active = null;
    m.pressed = null;
    m.lastWorld = null;
}

/**
 * Owns the listeners, pointer capture, rAF throttling and controls gating. The hit-test and
 * callbacks are caller-supplied.
 */
export function createPointerDragController<H>(options: PointerDragOptions<H>): StopHandle {
    const { canvas, pointerProjection, button = 0, onHover, onPointerLeave } = options;
    const machine = createDragMachine(options);

    const releaseCapture = (event: PointerEvent) => {
        if (canvas.hasPointerCapture(event.pointerId)) {
            canvas.releasePointerCapture(event.pointerId);
        }
    };

    const onPointerDown = (event: PointerEvent) => {
        if (event.button !== button) return;
        const { hit, world } = pointerProjection(event);
        if (hit === null) return;

        event.preventDefault();
        canvas.setPointerCapture(event.pointerId);

        beginPress(
            { hit, grabWorld: world, screenX: event.clientX, screenY: event.clientY },
            machine,
        );
    };

    const onPointerMove = (event: PointerEvent) => {
        const { hit, world } = pointerProjection(event);
        const consumed = moveTo({ world, screenX: event.clientX, screenY: event.clientY }, machine);
        if (consumed) return;
        onHover?.(hit, world, event);
    };

    const onPointerUp = (event: PointerEvent) => {
        releaseCapture(event);
        release(event, machine);
    };

    const onPointerCancel = (event: PointerEvent) => {
        releaseCapture(event);
        abort(machine);
    };

    const onLostPointerCapture = () => abort(machine);

    const onLeave = () => onPointerLeave?.();

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    canvas.addEventListener('lostpointercapture', onLostPointerCapture);
    canvas.addEventListener('pointerleave', onLeave);

    return () => {
        canvas.removeEventListener('pointerdown', onPointerDown);
        canvas.removeEventListener('pointermove', onPointerMove);
        canvas.removeEventListener('pointerup', onPointerUp);
        canvas.removeEventListener('pointercancel', onPointerCancel);
        canvas.removeEventListener('lostpointercapture', onLostPointerCapture);
        canvas.removeEventListener('pointerleave', onLeave);
        disposeMachine(machine);
    };
}
